import { AxiosMode } from '@/axiosModel';
import type { ApiResponse } from '@/axiosModel';

export interface InvoiceHistoryParams {
  current: number;
  size: number;
}

export interface InvoiceHistoryItem {
  newTime: string;
  amount: string;
  status: number | null;
  express: number | null;
  orderId: string;
  id: number | null;
  iType: number | null;
  title: string;
  number: string;
  rePhone: string;
  reEmail: string;
  regieterAddressTel: string;
  regieterTel: string;
  bankAccount: string;
  bankOpen: string;
  addressee: string;
  address: string;
  phone: string;
  postcode: string;
  remarks: string;
}

export interface InvoiceHistoryResult {
  list: InvoiceHistoryItem[];
  current: number;
  size: number;
  total: number;
}

export type InvoiceOrderRecord = Record<string, unknown> & {
  goods?: Record<string, unknown>[];
};

interface BackendInvoiceHistoryResult {
  list?: BackendInvoiceHistoryItem[];
  current?: number;
  size?: number;
  total?: number;
}

interface BackendInvoiceHistoryItem {
  newTime?: string | number;
  amount?: string | number;
  status?: string | number;
  express?: string | number;
  orderId?: string | number;
  id?: string | number;
  iType?: string | number;
  title?: string;
  number?: string | number;
  rePhone?: string | number;
  reEmail?: string;
  regieterAddressTel?: string;
  regieterTel?: string | number;
  bankAccount?: string | number;
  bankOpen?: string;
  addressee?: string;
  address?: string;
  phone?: string | number;
  postcode?: string | number;
  remarks?: string;
}

const apis = new AxiosMode();
const INVOICE_HISTORY_API = `${AxiosMode.API_HOST}/api/invoice/get-invoice-history`;
const INVOICE_ORDERS_API = `${AxiosMode.API_HOST}/api/invoice/get-invoice-orders`;
const APPLY_FOR_INVOICE_API = `${AxiosMode.API_HOST}/api/invoice/apply-for-invoice`;
const INVOICE_SYSTEM_TYPE = '2,9';

export const INVOICE_HISTORY_PAGE_SIZE = 40;

export interface ApplyForInvoiceParams {
  iType: number;
  number: string;
  title: string;
  reEmail: string;
  rePhone: string;
  remarks: string;
  regieterAddressTel: string;
  regieterTel: string;
  bankAccount: string;
  bankOpen: string;
  addressee: string;
  address: string;
  phone: string;
  postcode: string;
  pays: string;
}

export async function fetchInvoiceHistory(
  params: InvoiceHistoryParams
): Promise<InvoiceHistoryResult> {
  const current = toPositiveInteger(params.current, 1);
  const size = toPositiveInteger(params.size, INVOICE_HISTORY_PAGE_SIZE);
  const { req } = apis.post({
    postData: {
      current,
      size,
      system_type: INVOICE_SYSTEM_TYPE
    },
    remotePath: INVOICE_HISTORY_API
  });
  const res = (await req) as ApiResponse<BackendInvoiceHistoryResult>;
  const data = res.data ?? {};
  const records = Array.isArray(data.list) ? data.list : [];

  return {
    list: records.map(mapInvoiceHistoryItem),
    current: toPositiveInteger(data.current, current),
    size: toPositiveInteger(data.size, size),
    total: toNonNegativeInteger(data.total, records.length)
  };
}

export async function applyForInvoice(params: ApplyForInvoiceParams): Promise<void> {
  const { req } = apis.post({
    postData: params,
    remotePath: APPLY_FOR_INVOICE_API
  });

  await req;
}

export async function fetchInvoiceOrders(invoiceId: number): Promise<InvoiceOrderRecord[]> {
  const { req } = apis.post({
    postData: {
      invoiceId
    },
    remotePath: INVOICE_ORDERS_API
  });
  const res = (await req) as ApiResponse<unknown>;
  const records = Array.isArray(res.data) ? res.data.filter(isRecord) : [];

  return records.map((item) => ({
    ...item,
    goods: Array.isArray(item.goods) ? item.goods.filter(isRecord) : []
  }));
}

function mapInvoiceHistoryItem(item: BackendInvoiceHistoryItem): InvoiceHistoryItem {
  return {
    newTime: formatApiTime(item.newTime),
    amount: formatMoney(item.amount),
    status: toNullableNumber(item.status),
    express: toNullableNumber(item.express),
    orderId: toStringValue(item.orderId),
    id: toNullableNumber(item.id),
    iType: toNullableNumber(item.iType),
    title: toStringValue(item.title),
    number: toStringValue(item.number),
    rePhone: toStringValue(item.rePhone),
    reEmail: toStringValue(item.reEmail),
    regieterAddressTel: toStringValue(item.regieterAddressTel),
    regieterTel: toStringValue(item.regieterTel),
    bankAccount: toStringValue(item.bankAccount),
    bankOpen: toStringValue(item.bankOpen),
    addressee: toStringValue(item.addressee),
    address: toStringValue(item.address),
    phone: toStringValue(item.phone),
    postcode: toStringValue(item.postcode),
    remarks: toStringValue(item.remarks)
  };
}

function formatMoney(value: unknown) {
  if (value === undefined || value === null || value === '') return '0.00';

  const numberValue = Number(value);
  if (Number.isFinite(numberValue)) return numberValue.toFixed(2);

  return toStringValue(value) || '0.00';
}

function formatApiTime(value: unknown) {
  if (value === undefined || value === null || value === '') return '';
  if (typeof value === 'string' && Number.isNaN(Number(value))) return value;

  const timestamp = Number(value);
  if (!Number.isFinite(timestamp) || timestamp <= 0) return '';

  const normalizedTimestamp = timestamp < 1_000_000_000_000 ? timestamp * 1000 : timestamp;
  const date = new Date(normalizedTimestamp);
  if (Number.isNaN(date.getTime())) return '';

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function toStringValue(value: unknown) {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toNullableNumber(value: unknown) {
  if (value === undefined || value === null || value === '') return null;

  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}

function toPositiveInteger(value: unknown, fallback: number) {
  const numberValue = Number(value);
  return Number.isInteger(numberValue) && numberValue > 0 ? numberValue : fallback;
}

function toNonNegativeInteger(value: unknown, fallback: number) {
  const numberValue = Number(value);
  return Number.isInteger(numberValue) && numberValue >= 0 ? numberValue : fallback;
}

function pad(value: number) {
  return String(value).padStart(2, '0');
}
